import React from 'react';

import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import moment from 'moment';
import 'moment/locale/en-gb';
import 'moment/locale/de';

import i18n from '../../i18n';

export default class DialogDateTimeField extends React.Component {
    _committedDefault = false;

    formatOut(m) {
        return moment(m).format('YYYY-MM-DDTHH:mm:ss');
    }

    parseIn(v) {
        if (!v) return null;
        if (moment.isMoment(v) || v instanceof Date) {
            return moment(v);
        }
        const exact = moment(v, 'YYYY-MM-DDTHH:mm:ss', true);
        if (exact.isValid()) return exact;
        // Records come back as full ISO strings ("…T17:16:00.000Z"), which the
        // strict format above rejects, so an edit form opened with its dates
        // empty — and saving it would have cleared them. Read them as the grid
        // does (new Date): an offset is honoured, the time shown is local.
        const iso = moment(v, moment.ISO_8601, true);
        return iso.isValid() ? iso : null;
    }

    getDefaultMoment() {
        const { field } = this.props;
        if (!field || !field.default) return null;

        if (field.default === 'today') {
            return moment().startOf('day');
        }

        const asExact = moment(field.default, 'YYYY-MM-DDTHH:mm:ss', true);
        if (asExact.isValid()) return asExact;

        return null;
    }

    getPickerValue() {
        const { value } = this.props;
        const parsed = this.parseIn(value);
        if (parsed) return parsed;

        const def = this.getDefaultMoment();
        return def || null;
    }

    ensureDefaultCommitted() {
        const { value, field } = this.props;

        const isEmpty =
            value === null ||
            value === undefined ||
            value === '' ||
            (moment.isMoment(value) && !value.isValid());

        if (!isEmpty) {
            this._committedDefault = false;
            return;
        }

        if (this._committedDefault) return;

        const def = this.getDefaultMoment();
        if (!def) return;

        this._committedDefault = true;
        this.props.onChange(field.id, this.formatOut(def));
    }

    componentDidMount() {
        this.ensureDefaultCommitted();
    }

    componentDidUpdate(prevProps) {
        const valueBecameEmpty =
            prevProps.value !== this.props.value &&
            (this.props.value === null ||
                this.props.value === undefined ||
                this.props.value === '');

        const defaultChanged =
            prevProps.field?.default !== this.props.field?.default;

        if (valueBecameEmpty || defaultChanged) {
            this._committedDefault = false;
            this.ensureDefaultCommitted();
        }
    }

    handleDateChanged = (ev) => {
        if (!ev) {
            this.props.onChange(this.props.field.id, null);
            return;
        }
        const formattedDate = this.formatOut(ev);
        this.props.onChange(this.props.field.id, formattedDate);
    };

    render() {
        const { field } = this.props;

        const locale = i18n.language === 'en' ? 'en-gb' : i18n.language;
        const translatedName = i18n.t(`columns.${field.label}`);

        const pickerValue = this.getPickerValue();
        return (
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={locale}
            >
                <DateTimePicker
                    sx={{ width: '100%', my: 1 }}
                    id={this.props.field.id}
                    name={this.props.field.id}
                    label={translatedName}
                    value={pickerValue}
                    onChange={(ev) => this.handleDateChanged(ev)}
                    renderInput={(params) => <div {...params} />}
                    inputFormat="DD/MM/YYYY HH:mm"
                />
            </LocalizationProvider>
        );
    }
}
